import type {Reducer} from 'redux';
import produce from 'immer';
import type {
  JOB_INFORMATION,
  JOB_LISTS,
  JOB_CUSTOM_FIELDS,
  JOB_LOCATION,
  MILESTONES,
  INTERVIEW_FLOW,
  JOB_STAGES,
  USER_BASE_INFO,
  JOB_HIRING_TEAM,
  STAGE_REMOVE_RESPONSE,
  CompanyInfoJdGen,
  ApprovalObject,
  EmployeeRole,
  JOB_SCORE_CARDS,
  SCORE_CARD_CONFIG_RESPONSE,
  FEEDBACK_CONFIG_RESPONSE,
  STEP_QUESTIONNAIRE_SECTIONS_RESPONSE,
  JOB_HIRING_STEPS,
  PENDING_APPROVALS,
  CandidateList,
  ApprovalStatus,
  Tag,
  HIERARCHY_LEVEL,
  SCORECARD,
  EMAIL_AUTOMATION_TEMPLATE,
  JOB_POSITION,
  CANDIDATE_JOB_MASKING,
  FILTERED_JOB_LIST,
  FILTERED_JOB_PAYLOAD,
  FILTERED_JOB_SELECTED,
  OFFERS_CANDIDATE_INFO,
  RESUME_PARSING,
  Candidate,
  CANDIDATE_FEEDBACK,
  INTERVIEW_SCHEDULE,
  HM_EXPERIENCE_SURVEY,
  ApplicantList,
  ApplicantListFilter,
  ApplicantListFilterMetadata,
  MicrosoftLoginResponse,
  CandidateApplication,
  SCORECARD_PARAMETER,
} from 'src/types/crm';
import type {CrmJobActions} from 'src/action-creators/crm';
import type {CrmTagActions} from 'src/action-creators/crm-new/tag';
import type {CrmScorecardActions} from 'src/action-creators/crm-new/scorecard';
import type {CrmEmailAutomationActions} from 'src/action-creators/crm-new/email-automation';
import type {CrmJobPositionActions} from 'src/action-creators/crm-new/position-mapping';
import {GET_FILTERED_JOB, STORE_JOB_FILTERS} from 'src/action-creators/crm-new';

import {
  JOB_INFORMATION_INITIAL_STATE,
  UPDATE_JOB_INFORMATION,
  GET_JOB_LOCATIONS,
  GET_JOB_GRADES,
  GET_JOB_DEPARTMENTS,
  GET_CUSTOM_JOB_FIELDS,
  GET_JOB_HIRING_STAGES,
  GET_HIRING_MILESTONES,
  GET_JOB_INTERVIEW_FLOW,
  GET_HIRING_TEAM,
  REMOVE_HIRING_STAGE,
  UPDATE_COMPANY_INFO_JD_GENERATE,
  UPDATE_APPROVALS,
  GET_EMPLOYEE_ROLE,
  GET_JOB_SCORE_CARDS,
  GET_JOB_STEPS,
  GET_CANDIDATE_JOB_STEPS,
  GET_SCORE_CARD_CONFIG,
  UPDATE_SCORE_CARD_CONFIG,
  GET_FEEDBACK_CONFIG,
  GET_STEP_QUESTIONNAIRE_SECTIONS,
  GET_APPROVAL_REQUEST_TIME,
  SELECTED_QUESTIONNAIRE_SECTION,
  GET_JOB_MATCHES,
  GET_USER_INFO,
  GET_PENDING_APPROVALS,
  UPDATE_APPROVAL_STATUS,
  GET_TAGS,
  EDIT_TAG,
  DELETE_TAG,
  GET_HIERARCHY_LEVELS,
  ADD_TAG,
  UPDATE_SELECTED_PENDING_OFFER_CANDIDATE,
  UPDATE_APPLICANT_INFORMATION,
  UPDATE_CANDIDATE_FEEDBACKS,
  UPDATE_CANDIDATE_INTERVIEW_SCHEDULES,
  UPDATE_CANDIDATE_HM_EXPERIENCE_SURVEYS,
  UPDATE_COMPLETED_CANDIDATE_FEEDBACKS,
  UPDATE_COMPLETED_INTERVIEW_SCHEDULES,
  UPDATE_COMPLETED_HM_EXPERIENCE_SURVEYS,
  GET_INTERVIEWS_INFO,
  CRM_CANDIDATE_PARSING,
  GET_APPLICANT_LIST,
  GET_APPLICANT_LIST_FILTER_METADATA,
  GET_APPLICANT_DETAILS,
  MICROSOFT_LOGIN_SUCCESS,
  UPDATE_INTERNAL_FORM_CONFIG,
  GET_UNTRIED_COUNT,
} from 'src/utils/crm';

import {
  GET_ORG_SCORECARD,
  EDIT_SCORECARD_PARAMETER,
  ADD_SCORECARD_PARAMETER,
  DELETE_SCORECARD_PARAMETER,
  DELETE_SCORECARD_SECTION,
  ADD_SCORECARD_SECTION,
  EDIT_SCORECARD_SECTION,
} from 'src/action-creators/crm-new/scorecard';

import {
  GET_AUTOMATION_TEMPLATES,
  UPDATE_AUTOMATION_STATUS,
  GET_JOB_MASKING,
  UPDATE_JOB_MASKING,
} from 'src/action-creators/crm-new/email-automation';
import {
  GET_JOB_POSITIONS,
  ADD_JOB_POSITION,
  EDIT_JOB_POSITION,
} from 'src/action-creators/crm-new/position-mapping';


type State = {
  pending_approvals: PENDING_APPROVALS | null;
  user_info: USER_BASE_INFO | null;
  job_information: JOB_INFORMATION;
  grades: JOB_LISTS[] | null;
  locations: JOB_LOCATION[] | null;
  departments: JOB_LISTS[] | null;
  milestones: MILESTONES[] | null;
  jobCustomFields: JOB_CUSTOM_FIELDS[] | null;
  jobInterviewFlow: INTERVIEW_FLOW | null;
  jobInterviewStages: INTERVIEW_FLOW | null;
  jobHiringStages: JOB_STAGES | null;
  jobHiringTeam: JOB_HIRING_TEAM | null;
  removeStage: STAGE_REMOVE_RESPONSE | null;
  jdGenCompanyInfo: CompanyInfoJdGen | null;
  approvals: Array<ApprovalObject> | null;
  employeeRoles: EmployeeRole[] | null;
  jobScoreCards: JOB_SCORE_CARDS | null;
  jobScorecardConfigData: SCORE_CARD_CONFIG_RESPONSE[] | null;
  feedbackConfiguration: FEEDBACK_CONFIG_RESPONSE | null;
  stepQuestionnaireSection: STEP_QUESTIONNAIRE_SECTIONS_RESPONSE[] | null;
  approvalRequestTime: string | null;
  jobSteps: JOB_HIRING_STEPS[] | null;
  selectedQuestionnaireSection: JOB_STAGES | null;
  jobMatches: CandidateList | null;
  approvalStatus: ApprovalStatus | null;
  tags: Tag[] | null;
  hierarchyLevels: HIERARCHY_LEVEL[] | null;
  scorecard: SCORECARD | null;
  emailAutomationTemplates: EMAIL_AUTOMATION_TEMPLATE[] | null;
  jobPositions: JOB_POSITION[] | null;
  jobMasking: CANDIDATE_JOB_MASKING | null;
  filteredJobList: FILTERED_JOB_LIST | null;
  selectedPendingOfferCandidate: OFFERS_CANDIDATE_INFO | null;
  applicantInformation: Candidate | null;
  candidateJobSteps: JOB_HIRING_STEPS[] | null;
  candidateParsing: RESUME_PARSING | null;
  candidateFeedbacks: CANDIDATE_FEEDBACK[] | null;
  candidateInterviewSchedules: INTERVIEW_SCHEDULE[] | null;
  candidateHmExperienceSurveys: HM_EXPERIENCE_SURVEY[] | null;
  completedCandidateFeedbacks: CANDIDATE_FEEDBACK[] | null;
  completedInterviewSchedules: INTERVIEW_SCHEDULE[] | null;
  completedHmExperienceSurveys: HM_EXPERIENCE_SURVEY[] | null;
  applicantList: ApplicantList | null;
  applicantListFilterMetadata: ApplicantListFilterMetadata | null;
  applicantDetails: Candidate[] | null;
  microsoftLoginResponse: MicrosoftLoginResponse | null;
  untriedCount: number | null;
  storedJobFilters: {
    selectedFilters: FILTERED_JOB_PAYLOAD | null;
    rawFilteredSelected: FILTERED_JOB_SELECTED | null;
    selectedJobStatuses: string[] | null;
    searchedText: string;
    isMyJobSelected: boolean;
  };
};

const initialState = {
  user_info: null,
  pending_approvals: null,
  job_information: JOB_INFORMATION_INITIAL_STATE,
  applicantInformation: null,
  grades: null,
  locations: null,
  departments: null,
  milestones: null,
  jobInterviewFlow: null,
  jobCustomFields: null,
  jobHiringTeam: null,
  jobInterviewStages: null,
  jobHiringStages: null,
  removeStage: null,
  jdGenCompanyInfo: null,
  approvals: null,
  employeeRoles: null,
  jobScoreCards: null,
  jobScorecardConfigData: null,
  feedbackConfiguration: null,
  stepQuestionnaireSection: null,
  approvalRequestTime: null,
  jobSteps: null,
  selectedQuestionnaireSection: null,
  jobMatches: null,
  approvalStatus: null,
  tags: null,
  hierarchyLevels: null,
  scorecard: null,
  emailAutomationTemplates: null,
  positionMapping: null,
  jobMasking: null,
  filteredJobList: null,
  selectedPendingOfferCandidate: null,
  candidateJobSteps: null,
  candidateParsing: null,
  candidateFeedbacks: null,
  candidateInterviewSchedules: null,
  candidateHmExperienceSurveys: null,
  completedCandidateFeedbacks: null,
  completedInterviewSchedules: null,
  completedHmExperienceSurveys: null,
  applicantList: null,
  applicantListFilterMetadata: null,
  applicantDetails: null,
  microsoftLoginResponse: null,
  untriedCount: null,
  storedJobFilters: {
    selectedFilters: null,
    rawFilteredSelected: null,
    selectedJobStatuses: null,
    searchedText: '',
    isMyJobSelected: true,
  },
} as const;

// @ts-expect-error - TS2322 - Type '(state: State | null | undefined, action: CrmJobActions | CrmTagActions | CrmScorecardActions | CrmEmailAutomationActions | CrmJobPositionActions) => { ...; } | ... 44 more ... | null' is not assignable to type 'Reducer<State, CrmJobActions | CrmTagActions | CrmScorecardActions | CrmJobPositionActions>'.
const reducer: Reducer<
  State,
  CrmJobActions | CrmTagActions | CrmScorecardActions | CrmJobPositionActions
> = (
  // @ts-expect-error - TS2741 - Property 'jobPositions' is missing in type '{ readonly user_info: null; readonly pending_approvals: null; readonly job_information: { readonly id: 0; readonly createdBy: 0; readonly createdOn: 0; readonly updatedBy: 0; readonly updatedOn: 0; ... 65 more ...; readonly workplaceType: null; }; ... 42 more ...; readonly microsoftLoginResponse: null; }' but required in type 'State'.
  state: State = initialState,
  action:
    | CrmJobActions
    | CrmTagActions
    | CrmScorecardActions
    | CrmEmailAutomationActions
    | CrmJobPositionActions,
) => {
  switch (action.type) {
    case UPDATE_JOB_INFORMATION:
      return {
        ...state,
        job_information: {
          ...action.payload,
        },
      };
    case GET_JOB_LOCATIONS:
      return {
        ...state,
        locations: action.payload,
      };
    case GET_JOB_GRADES:
      return {
        ...state,
        grades: action.payload,
      };
    case GET_JOB_DEPARTMENTS:
      return {
        ...state,
        departments: action.payload,
      };
    case GET_HIRING_MILESTONES:
      return {
        ...state,
        milestones: action.payload,
      };
    case GET_CUSTOM_JOB_FIELDS:
      return {
        ...state,
        jobCustomFields: action.payload,
      };
    case GET_JOB_INTERVIEW_FLOW:
      return {
        ...state,
        jobInterviewFlow: action.payload,
      };
    case GET_JOB_HIRING_STAGES:
      return {
        ...state,
        jobInterviewFlow: {...state.jobInterviewFlow, stages: action.payload},
      };
    case GET_HIRING_TEAM:
      return {
        ...state,
        jobHiringTeam: action.payload,
      };
    case REMOVE_HIRING_STAGE:
      return {
        ...state,
        removeStage: action.payload,
      };
    case UPDATE_COMPANY_INFO_JD_GENERATE:
      return {
        ...state,
        jdGenCompanyInfo: action.payload,
      };
    case UPDATE_APPROVALS:
      return {
        ...state,
        approvals: action.payload,
      };
    case GET_EMPLOYEE_ROLE:
      return {
        ...state,
        employeeRoles: action.payload,
      };
    case GET_JOB_STEPS:
      return {
        ...state,
        jobSteps: action.payload,
      };
    case GET_CANDIDATE_JOB_STEPS:
      return {
        ...state,
        candidateJobSteps: action.payload,
      };
    case GET_JOB_SCORE_CARDS:
      return {
        ...state,
        jobScoreCards: action.payload,
      };
    case GET_SCORE_CARD_CONFIG:
      return {
        ...state,
        jobScorecardConfigData: action.payload,
      };
    case UPDATE_SCORE_CARD_CONFIG:
      return {
        ...state,
        jobScorecardConfigData: state.jobScorecardConfigData?.map(
          (section: SCORE_CARD_CONFIG_RESPONSE) => {
            return section.id === action.payload.id ? action.payload : section;
          },
        ),
      };
    case GET_FEEDBACK_CONFIG:
      return {
        ...state,
        feedbackConfiguration: action.payload,
      };
    case GET_STEP_QUESTIONNAIRE_SECTIONS:
      return {
        ...state,
        stepQuestionnaireSection: action.payload,
      };
    case GET_APPROVAL_REQUEST_TIME:
      return {
        ...state,
        approvalRequestTime: action.payload,
      };

    case SELECTED_QUESTIONNAIRE_SECTION:
      return {
        ...state,
        selectedQuestionnaireSection: action.payload,
      };
    case GET_JOB_MATCHES:
      return {
        ...state,
        jobMatches: action.payload,
      };
    case GET_USER_INFO:
      return {
        ...state,
        user_info: action.payload,
      };
    case GET_PENDING_APPROVALS:
      return {
        ...state,
        pending_approvals: action.payload,
      };
    case UPDATE_APPROVAL_STATUS:
      return {
        ...state,
        approvalStatus: action.payload,
      };
    case GET_TAGS:
      return {
        ...state,
        tags: action.payload,
      };
    case EDIT_TAG:
      const newTags = (state.tags || []).map((tag) => {
        if (tag.id === action.payload.id) {
          return {
            ...action.payload,
          };
        }
        return tag;
      });
      return {
        ...state,
        tags: newTags,
      };
    // @ts-expect-error - TS2678 - Type '"add-tag"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case ADD_TAG:
      return {
        ...state,
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS18047 - 'state' is possibly 'null'. | TS2339 - Property 'payload' does not exist on type 'never'. | TS2339 - Property 'payload' does not exist on type 'never'.
        tags: state.tags ? [...state.tags, action.payload] : [action.payload],
      };
    case DELETE_TAG:
      const updatedTags = state.tags
        ? // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS2367 - This comparison appears to be unintentional because the types 'number' and 'Tag' have no overlap.
          state.tags.filter((tag) => tag.id !== action.payload)
        : [];
      return {
        ...state,
        tags: updatedTags,
      };
    case GET_HIERARCHY_LEVELS:
      return {
        ...state,
        hierarchyLevels: action.payload,
      };
    case GET_ORG_SCORECARD:
      return {
        ...state,
        scorecard: action.payload,
      };
    case EDIT_SCORECARD_PARAMETER:
      return produce(state, (draft) => {
        const section =
          draft.scorecard &&
          draft.scorecard.sections &&
          draft.scorecard.sections.find(
            (section) => section.id === action.payload.sectionId,
          );

        if (section) {
          const parameter = section.parameters?.find(
            (parameter: SCORECARD_PARAMETER) =>
              parameter.id === action.payload.id,
          );

          if (parameter) {
            Object.assign(parameter, action.payload);
          }
        }
      });

    case ADD_SCORECARD_PARAMETER:
      return produce(state, (draft) => {
        const section =
          draft.scorecard &&
          draft.scorecard.sections &&
          draft.scorecard.sections.find(
            (section) => section.id === action.payload.sectionId,
          );

        if (section) {
          section.parameters?.push(action.payload);
        }
      });
    case DELETE_SCORECARD_PARAMETER:
      return produce(state, (draft) => {
        const section =
          draft.scorecard &&
          draft.scorecard.sections &&
          draft.scorecard.sections.find(
            (section) => section.id === action.payload.sectionId,
          );

        if (section) {
          section.parameters = section.parameters?.filter(
            (parameter: SCORECARD_PARAMETER) =>
              parameter.id !== action.payload.id,
          );
        }
      });

    case DELETE_SCORECARD_SECTION:
      return produce(state, (draft) => {
        const scorecard = draft.scorecard;
        if (scorecard !== null && scorecard.sections) {
          scorecard.sections = scorecard.sections.filter(
            (section) => section.id !== action.payload.id,
          );
        }
      });
    case ADD_SCORECARD_SECTION:
      return produce(state, (draft) => {
        if (draft.scorecard) {
          draft.scorecard.sections = [
            ...(draft.scorecard.sections || []),
            ...action.payload,
          ];
        }
      });

    case EDIT_SCORECARD_SECTION:
      return produce(state, (draft) => {
        const section =
          draft.scorecard &&
          draft.scorecard.sections &&
          draft.scorecard.sections.find(
            (section) => section.id === action.payload.id,
          );

        if (section) {
          Object.assign(section, action.payload);
        }
      });
    case GET_AUTOMATION_TEMPLATES:
      return {
        ...state,
        emailAutomationTemplates: action.payload,
      };
    case UPDATE_AUTOMATION_STATUS:
      return {
        ...state,
        emailAutomationTemplates: (state.emailAutomationTemplates || []).map(
          (template) => {
            if (template.id === action.payload.id) {
              return {
                ...template,
                status: action.payload.status,
              };
            }
            return template;
          },
        ),
      };
    case GET_JOB_POSITIONS:
      return {
        ...state,
        jobPositions: action.payload,
      };
    case EDIT_JOB_POSITION:
      return produce(state, (draft) => {
        const position = draft.jobPositions?.find(
          (pos: JOB_POSITION) => pos.id === action.payload.id,
        );

        if (position) {
          Object.assign(position, action.payload);
        }
      });
    // @ts-expect-error - TS2678 - Type '"add-job-position"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case ADD_JOB_POSITION:
      return {
        ...state,
        jobPositions: state.jobPositions
          ? // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS2339 - Property 'payload' does not exist on type 'never'.
            [...state.jobPositions, action.payload]
          : // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
            [action.payload],
      };
    // @ts-expect-error - TS2678 - Type '"get-job-masking"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case GET_JOB_MASKING:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        jobMasking: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"update-job-masking"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case UPDATE_JOB_MASKING:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        jobMasking: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"get-filtered-job"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case GET_FILTERED_JOB:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        filteredJobList: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"store-job-filters"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case STORE_JOB_FILTERS:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        storedJobFilters: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"update-selected-candidate"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case UPDATE_SELECTED_PENDING_OFFER_CANDIDATE:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        selectedPendingOfferCandidate: action.payload,
      };
    case UPDATE_APPLICANT_INFORMATION:
      return {
        ...state,
        applicantInformation: {
          ...action.payload,
        },
      };
    case UPDATE_CANDIDATE_FEEDBACKS:
      return {
        ...state,
        candidateFeedbacks: action.payload,
      };
    case UPDATE_CANDIDATE_INTERVIEW_SCHEDULES:
      return {
        ...state,
        candidateInterviewSchedules: action.payload,
      };
    case UPDATE_CANDIDATE_HM_EXPERIENCE_SURVEYS:
      return {
        ...state,
        candidateHmExperienceSurveys: action.payload,
      };
    case UPDATE_COMPLETED_CANDIDATE_FEEDBACKS:
      return {
        ...state,
        completedCandidateFeedbacks: action.payload,
      };
    case UPDATE_COMPLETED_INTERVIEW_SCHEDULES:
      return {
        ...state,
        completedInterviewSchedules: action.payload,
      };
    case UPDATE_COMPLETED_HM_EXPERIENCE_SURVEYS:
      return {
        ...state,
        completedHmExperienceSurveys: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"crm-candidate-parsing"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case CRM_CANDIDATE_PARSING:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        candidateParsing: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"get-applicant-list"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case GET_APPLICANT_LIST:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        applicantList: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"get-applicant-list-filter-metadata"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case GET_APPLICANT_LIST_FILTER_METADATA:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        applicantListFilterMetadata: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"get-applicant-details"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case GET_APPLICANT_DETAILS:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        applicantDetails: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"microsoft-login-success"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case MICROSOFT_LOGIN_SUCCESS:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        microsoftLoginResponse: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"update-internal-form-config"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case UPDATE_INTERNAL_FORM_CONFIG:
      // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
      const updatedStepCandidate = action.payload || {};
      const jobId = updatedStepCandidate.jobId;
      const stepCandidateId = updatedStepCandidate.stepId;
      const newJobInternalForm = updatedStepCandidate.jobInternalForm || {};
      const updatedApplications = state.applicantInformation?.applications?.map(
        (app: CandidateApplication) => {
          if (app.jobId === jobId) {
            let stepExists = false;
            const updatedStepCandidates = app.stepCandidates.map((sc) => {
              if (sc.stepId === stepCandidateId) {
                stepExists = true;
                return {
                  ...sc,
                  jobInternalForm: newJobInternalForm,
                };
              }
              return sc;
            });

            if (!stepExists) {
              updatedStepCandidates.push(updatedStepCandidate);
            }

            return {
              ...app,
              stepCandidates: updatedStepCandidates,
            };
          }
          return app;
        },
      );

      return {
        ...state,
        applicantInformation: {
          ...state.applicantInformation,
          applications: updatedApplications || [],
        },
      };
    case GET_UNTRIED_COUNT:
      return {
        ...state,
        untriedCount: action.payload,
      };
  }

  return state;
};

export default reducer;
